/**
 * 消費詳細の表。見出しを押すと、その列で昇順・降順に並べ替える。
 * 初めの並びがどの列の順かを示すため、その列（initialSort）の見出しに初めから並べ替えの印を付ける。
 * 行の下に行を並べる表（機能ごとの内訳）では、初めは上の行だけを表示し、行ごとか、すべてまとめて下の行を表示できる。
 * まとめて開け閉めする印は、行の印と同じ列の見出しに置く（expandAll の列）。
 * 並べ替えても、下の行はその行の下に置き、下の行どうしも同じ列で並べ替える。
 */

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import { compactTableSx } from '@/components/credit-contracts/tableParts';

import type { DetailColumn, RowContext } from './detailColumns';
import { ExpandAllButton } from './FeatureLabel';
import { GroupedHeader } from './GroupedHeader';
import { nextSort, sortRows, type SortState } from './sorting';

interface Props<T> {
  ariaLabel: string;
  columns: DetailColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage: string;
  /** 値の行だけをスクロールする高さの上限 */
  maxHeight?: number | string;
  sortable?: boolean;
  /** サーバーが返した並びが、どの列の順か。その列で並べ替えても並びは変わらない */
  initialSort?: SortState;
  selectedKey?: string;
  onRowClick?: (row: T) => void;
  /** 行の下に並べる行 */
  subRows?: (row: T) => T[];
}

const numberCell = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

const LEAF: Omit<RowContext, 'depth'> = { expanded: null, toggle: () => {} };

export function DetailTable<T>({
  ariaLabel,
  columns,
  rows,
  rowKey,
  emptyMessage,
  maxHeight,
  sortable = true,
  initialSort,
  selectedKey,
  onRowClick,
  subRows,
}: Props<T>) {
  const [sort, setSort] = useState<SortState | null>(initialSort ?? null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const order = useCallback(
    (list: T[]) => {
      const col = sort && columns.find((c) => c.header.key === sort.key);
      return sort && col ? sortRows(list, col.sortValue, sort.dir) : list;
    },
    [columns, sort],
  );
  const shown = useMemo(() => order(rows), [order, rows]);
  const parents = useMemo(
    () => (subRows ? rows.filter((r) => subRows(r).length > 0).map(rowKey) : []),
    [rows, rowKey, subRows],
  );
  const allOpen = parents.length > 0 && parents.every((k) => open.has(k));
  const expandAll =
    parents.length > 0 ? (
      <ExpandAllButton
        expanded={allOpen}
        onToggle={() => setOpen(allOpen ? new Set() : new Set(parents))}
      />
    ) : undefined;
  const header = columns.map((c) => ({
    ...c.header,
    sortable: sortable && c.header.sortable,
    leading: c.expandAll ? expandAll : undefined,
  }));
  const toggle = (key: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  const renderRow = (row: T, key: string, context: RowContext) => (
    <TableRow
      key={key}
      hover={!!onRowClick}
      selected={selectedKey === key}
      aria-selected={onRowClick ? selectedKey === key : undefined}
      sx={onRowClick ? { cursor: 'pointer' } : undefined}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {columns.map((c) => (
        <TableCell key={c.header.key} sx={c.header.numeric ? numberCell : undefined}>
          <Box component="span">{c.render(row, context)}</Box>
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight, overflow: 'auto' }}>
      <Table stickyHeader size="small" aria-label={ariaLabel} sx={compactTableSx}>
        <TableHead>
          <GroupedHeader
            columns={header}
            sort={sort}
            onSort={(key, numeric) => setSort((current) => nextSort(current, key, numeric))}
          />
        </TableHead>
        <TableBody>
          {shown.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                <Typography variant="body2" color="text.secondary" py={1}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {shown.flatMap((row) => {
            const key = rowKey(row);
            const children = subRows?.(row) ?? [];
            const expanded = children.length > 0 ? open.has(key) : null;
            return [
              renderRow(row, key, { depth: 0, expanded, toggle: () => toggle(key) }),
              ...(expanded
                ? order(children).map((child) =>
                    renderRow(child, rowKey(child), { depth: 1, ...LEAF }),
                  )
                : []),
            ];
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
